// B-101 — a zip dropped with the day's reports, opened in the browser into the files it holds.
//
// The day can come as one zip (the folder zipped, or the bank's own `conciliacion e pago
// DDMMAAAA.zip`). Each file inside keeps its path within the zip (`rutaRelativa`), so its folder can
// still say which office it is from. A zip inside the zip is opened too, one level down — the bank's
// zip inside a zipped folder — and no further. Limits keep a hostile or broken archive from taking
// the tab down; past them nothing of that zip is used and the person is told. Nothing is written
// anywhere: the files only live in memory until they are sent.

import { rutaDeArchivo } from "./cierreCargaAgrupada";

export const MAX_ARCHIVOS_ZIP = 300;
export const MAX_BYTES_ZIP = 200 * 1024 * 1024;
const PROFUNDIDAD = 2;

export const esZip = (archivo) => /\.zip$/i.test(String(archivo?.name || ""));

// A file worth keeping out of a zip: not a folder, not macOS's shadow files, not a lock or hidden file.
export const entradaUtil = (nombre) => {
  const base = String(nombre || "").split("/").pop();
  if (!base || /(^|\/)__MACOSX\//.test(nombre)) return false;
  return !base.startsWith("~$") && !base.startsWith(".");
};

async function abrir(archivo, JSZip) {
  // A File's bytes; JSZip also reads a Blob itself, for a File with no `arrayBuffer()`.
  const zip = await JSZip.loadAsync(typeof archivo.arrayBuffer === "function" ? await archivo.arrayBuffer() : archivo);
  const entradas = Object.values(zip.files).filter((e) => !e.dir && entradaUtil(e.name));
  if (entradas.length > MAX_ARCHIVOS_ZIP) {
    throw new Error(`${archivo.name} trae ${entradas.length} archivos; se abren hasta ${MAX_ARCHIVOS_ZIP}.`);
  }
  const prefijo = rutaDeArchivo(archivo).replace(/\.zip$/i, "");
  const dentro = [];
  let total = 0;
  for (const entrada of entradas) {
    // eslint-disable-next-line no-await-in-loop
    const contenido = await entrada.async("blob");
    total += contenido.size;
    if (total > MAX_BYTES_ZIP) throw new Error(`${archivo.name} pesa más de ${MAX_BYTES_ZIP / (1024 * 1024)} MB al abrirlo.`);
    const archivoDentro = new File([contenido], entrada.name.split("/").pop(), {
      lastModified: entrada.date instanceof Date ? entrada.date.getTime() : archivo.lastModified,
    });
    Object.defineProperty(archivoDentro, "rutaRelativa", { value: `${prefijo}/${entrada.name}` });
    dentro.push(archivoDentro);
  }
  return dentro;
}

/**
 * The files, with every zip among them replaced by what it holds.
 * @returns {Promise<{ archivos: File[], errores: string[] }>} a zip that cannot be opened is left
 *   out and said in `errores`, never half used
 */
export async function expandirZips(archivos, { cargarJSZip = () => import("jszip").then((m) => m.default || m) } = {}) {
  let pendientes = Array.from(archivos || []);
  const errores = [];
  let JSZip = null;
  for (let nivel = 0; nivel < PROFUNDIDAD && pendientes.some(esZip); nivel += 1) {
    const siguientes = [];
    for (const archivo of pendientes) {
      if (!esZip(archivo)) {
        siguientes.push(archivo);
        continue; // eslint-disable-line no-continue
      }
      try {
        // eslint-disable-next-line no-await-in-loop
        JSZip = JSZip || (await cargarJSZip());
        // eslint-disable-next-line no-await-in-loop
        siguientes.push(...(await abrir(archivo, JSZip)));
      } catch (e) {
        errores.push(e.message && e.message.includes(archivo.name) ? e.message : `No se pudo abrir ${archivo.name}.`);
      }
    }
    pendientes = siguientes;
  }
  return { archivos: pendientes, errores };
}
